import React from "react";
import styles from "./SearchFilters.module.scss";
import SingleSelectFilter from "./singleSelectFilter/SingleSelectFilter";
import TagsFilter from "./tagsFilter/TagsFilter";
import CategoryFilter from "./categoriesFilter/CategoryFilter";
import MultiSelectFilter from "./multiSelectFilter/MultiSelectFilter";
import { Filter } from "../types/SearchData";

interface SearchFiltersProps {
  filters: Filter[];
  selectedFilters: { [key: string]: string[] }; // Unified to string[]
  setSelectedFilters: (filters: { [key: string]: string[] }) => void;
  labels: { [key: string]: string };
  isMobile: boolean;
}

const SearchFilters: React.FC<SearchFiltersProps> = ({ filters, selectedFilters, setSelectedFilters, labels, isMobile }) => {
  // Utility function to update filter state
  const updateFilterState = (filterId: string, updater: (current: string[]) => string[]) => {
    setSelectedFilters({
      ...selectedFilters,
      [filterId]: updater(selectedFilters[filterId] || []), // Ensure default is string[]
    });
  };

  // Handlers for different filter types
  const handleOptionSelect = (filterId: string, labels: string[]) => {
    updateFilterState(filterId, () => labels); // Accept string[]
  };

  const handleTagSelect = (filterId: string, tag: string) => {
    updateFilterState(filterId, (current) => {
      const currentSelection = current as string[];
      return currentSelection.includes(tag)
        ? currentSelection.filter((item) => item !== tag)
        : [...currentSelection, tag];
    });
  };

    const handleCategorySelect = (filterId: string, categories: string[]) => {
        updateFilterState(filterId, (current) => {
            const currentSelection = current as string[];
            const isParentSelected = currentSelection.includes(categories[0]);
            if (isParentSelected) {
                return currentSelection.filter((item) => !categories.includes(item));
            } else {
                return Array.from(new Set([...currentSelection, ...categories]));
            }
        });
    };

  const handleMultiSelect = (filterId: string, labels: string[]) => {
    updateFilterState(filterId, () => labels);
  };

  const handleOptionToggle = (filterId: string, label: string) => {
    updateFilterState(filterId, (current) => {
      const currentSelection = current as string[];
      return currentSelection.includes(label)
        ? currentSelection.filter((item) => item !== label)
        : [...currentSelection, label];
    });
  };

  // Render logic for each filter type
  const renderFilter = (filter: Filter) => {
    if (
      ((filter?.type === "singleSelect" || filter?.type === "multiSelect") &&
        (!filter?.data?.options || filter?.data?.options.length === 0)) ||
      (filter?.type === "tags" && (!filter?.data?.tags || filter?.data?.tags.length === 0)) ||
      (filter?.type === "categories" && (!filter?.data?.categories || filter?.data?.categories.length === 0))
    ) {
      return null;
    }

    switch (filter?.type) {
      case "singleSelect":
        return (
          <SingleSelectFilter
            key={filter?.indexPropertyName}
            title={filter?.data?.title}
            options={filter?.data?.options}
            filterId={filter?.filterId}
            indexPropertyName={filter?.indexPropertyName}
            selectedOption={(selectedFilters[filter?.indexPropertyName]?.[0] as string) || null}
            onOptionSelect={handleOptionSelect}
          />
        );
      case "tags":
        return (
          <TagsFilter
            key={filter?.indexPropertyName}
            title={filter?.data?.title}
            tags={filter?.data?.tags}
            filterId={filter?.filterId}
            indexPropertyName={filter?.indexPropertyName}
            selectedTags={(selectedFilters[filter?.indexPropertyName] as string[]) || []}
            onTagSelect={handleTagSelect}
          />
        );
      case "categories":
        return (
          <CategoryFilter
            key={filter?.indexPropertyName}
            title={filter?.data?.title}
            categories={filter?.data?.categories}
            filterId={filter?.filterId}
            indexPropertyName={filter?.indexPropertyName}
            selectedCategories={(selectedFilters[filter?.indexPropertyName] as string[]) || []}
            onCategorySelect={handleCategorySelect}
            debounceDelay={600}
          />
        );
      case "multiSelect":
        return (
          <MultiSelectFilter
            key={filter?.indexPropertyName}
            title={filter?.data?.title}
            options={filter?.data?.options}
            filterId={filter?.filterId}
            indexPropertyName={filter?.indexPropertyName}
            selectedOptions={(selectedFilters[filter?.indexPropertyName] as string[]) || []}
            onMultiSelect={handleMultiSelect}
            onOptionToggle={handleOptionToggle}
            labels={labels}
          />
        );
      default:
        return null;
    }
  };

    return <div className={styles["filters-container"]}>
        {!isMobile && labels["FiltersTitleLabel"] &&
            <h2 className={styles["filters-title-label"]}>{labels["FiltersTitleLabel"]}</h2>
        }
        {filters?.map(renderFilter)}
    </div>;
};

export default SearchFilters;
